import React, { useState } from 'react';
import { NotebookPage, NotebookFolder } from '../../pages/NotebookPage';

interface PageListProps {
  pages: NotebookPage[];
  selectedPage: NotebookPage | null;
  selectedFolder: NotebookFolder | null;
  onSelectPage: (page: NotebookPage) => void;
  onCreatePage: (folderId: string) => void;
  onDeletePage: (pageId: string) => void;
  onUpdateFolder: (folderData: Partial<NotebookFolder>) => void;
  onDeleteFolder: (folderId: string) => void;
  loading: boolean;
}

const PageList: React.FC<PageListProps> = ({
  pages,
  selectedPage,
  selectedFolder,
  onSelectPage,
  onCreatePage,
  onDeletePage,
  onUpdateFolder,
  onDeleteFolder,
  loading
}) => {
  const [isEditingFolderName, setIsEditingFolderName] = useState(false);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showFolderDeleteConfirm, setShowFolderDeleteConfirm] = useState(false);

  const handleStartEditingFolder = () => {
    if (selectedFolder) {
      setEditingFolderName(selectedFolder.name);
      setIsEditingFolderName(true);
    }
  };

  const handleSaveFolderName = async () => {
    if (selectedFolder && editingFolderName.trim()) {
      try {
        await onUpdateFolder({
          id: selectedFolder.id,
          name: editingFolderName.trim()
        });
        setIsEditingFolderName(false);
      } catch (error) {
        console.error('Failed to update folder name:', error);
      }
    }
  };

  const handleCancelEditingFolder = () => {
    setIsEditingFolderName(false);
    setEditingFolderName('');
  };

  const handleDeletePage = async (pageId: string) => {
    try {
      await onDeletePage(pageId);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete page:', error);
    }
  };

  const handleDeleteFolder = async () => {
    if (selectedFolder) {
      try {
        await onDeleteFolder(selectedFolder.id);
        setShowFolderDeleteConfirm(false);
      } catch (error) {
        console.error('Failed to delete folder:', error);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent.length > maxLength 
      ? textContent.substring(0, maxLength) + '...'
      : textContent;
  };

  if (!selectedFolder) {
    return (
      <div className="w-full h-full bg-white border-r border-gray-200 flex items-center justify-center">
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-2">📁</div>
          <p className="text-sm">Select a folder to view pages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white border-r border-gray-200 flex flex-col">
      {/* Header with folder name, page count, and actions */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1">
            {isEditingFolderName ? (
              <div className="flex items-center space-x-2 flex-1">
                <input
                  type="text"
                  value={editingFolderName}
                  onChange={(e) => setEditingFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveFolderName();
                    } else if (e.key === 'Escape') {
                      handleCancelEditingFolder();
                    }
                  }}
                  className="flex-1 px-2 py-1 text-lg font-semibold bg-white border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <button
                  onClick={handleSaveFolderName}
                  className="p-1 text-green-600 hover:text-green-700"
                  title="Save"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={handleCancelEditingFolder}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Cancel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <h2 
                  className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                  onClick={handleStartEditingFolder}
                  title="Click to edit folder name"
                >
                  {selectedFolder.name}
                </h2>
                <span className="text-sm text-gray-500">
                  • {pages.length} page{pages.length !== 1 ? 's' : ''}
                </span>
              </>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onCreatePage(selectedFolder.id)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center space-x-1"
            >
              <span>+</span>
              <span>New</span>
            </button>
            
            <button
              onClick={() => setShowFolderDeleteConfirm(true)}
              className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
              title="Delete folder"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Pages List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : pages.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <div className="text-3xl mb-2">📄</div>
              <p className="text-sm">No pages yet</p>
              <p className="text-xs text-gray-400 mt-1">Click "New" to create your first page</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {pages.map((page) => (
              <div
                key={page.id}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors group ${
                  selectedPage?.id === page.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                }`}
                onClick={() => onSelectPage(page)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">
                      {page.title || 'Untitled Note'}
                    </h3>
                    {page.content && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {truncateContent(page.content)}
                      </p>
                    )}
                    <div className="flex items-center space-x-2 mt-2 text-xs text-gray-400">
                      <span>{formatDate(page.updatedAt)}</span>
                      {page.linkedClients.length > 0 && (
                        <>
                          <span>•</span>
                          <span>{page.linkedClients.length} client{page.linkedClients.length !== 1 ? 's' : ''}</span>
                        </>
                      )}
                      {page.linkedTranscripts.length > 0 && (
                        <>
                          <span>•</span>
                          <span>{page.linkedTranscripts.length} transcript{page.linkedTranscripts.length !== 1 ? 's' : ''}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(page.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-all"
                    title="Delete page"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Page Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Page</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete this page? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => handleDeletePage(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirmation Modal */}
      {showFolderDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Folder</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete "{selectedFolder.name}" and all {pages.length} page{pages.length !== 1 ? 's' : ''} inside it? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleDeleteFolder}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Delete All
              </button>
              <button
                onClick={() => setShowFolderDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageList; 